import type { Patient, PatientSex } from '@/entities/patient/types'

const DAY_IN_MS = 24 * 60 * 60 * 1000

/** Calcula idade em dias a partir da data de nascimento. Retorna null se data inválida ou futura. */
export const calculateAgeInDays = (
  birthDateValue: string,
  referenceDate: Date = new Date(),
): number | null => {
  const birthDate = new Date(birthDateValue)
  if (Number.isNaN(birthDate.getTime())) return null
  const days = Math.floor((referenceDate.getTime() - birthDate.getTime()) / DAY_IN_MS)
  return days < 0 ? null : days
}

export const calculateAgeFromBirthDate = (
  birthDateValue: string,
  referenceDate: Date = new Date(),
): number | null => {
  const days = calculateAgeInDays(birthDateValue, referenceDate)
  return days == null ? null : Math.floor(days / 365.25)
}

/** Calcula idade em semanas a partir da data de nascimento. */
export const calculateAgeInWeeks = (
  birthDateValue: string,
  referenceDate: Date = new Date(),
): number | null => {
  const days = calculateAgeInDays(birthDateValue, referenceDate)
  return days == null ? null : Math.floor(days / 7)
}

/** Calcula idade em meses a partir da data de nascimento (0–11 para menos de 1 ano). */
export const calculateAgeInMonths = (
  birthDateValue: string,
  referenceDate: Date = new Date(),
): number | null => {
  const birthDate = new Date(birthDateValue)
  if (Number.isNaN(birthDate.getTime())) return null
  const ref = referenceDate
  let months = (ref.getFullYear() - birthDate.getFullYear()) * 12
  months += ref.getMonth() - birthDate.getMonth()
  if (ref.getDate() < birthDate.getDate()) months--
  return months < 0 ? null : months
}

export interface FormatAgeOptions {
  birthDate?: string | null
  ageYears?: number | null
}

/** Formata idade para exibição: "X semanas" (&lt; 1 mês), "X meses" (&lt; 1 ano), "X anos" caso contrário. */
export const formatAgeForDisplay = (
  options: FormatAgeOptions,
  referenceDate: Date = new Date(),
): string | null => {
  const { birthDate, ageYears } = options
  if (birthDate) {
    const days = calculateAgeInDays(birthDate, referenceDate)
    if (days == null) return null
    if (days < 30) {
      const weeks = Math.floor(days / 7)
      if (weeks === 0) return 'menos de 1 semana'
      return `${weeks} ${weeks === 1 ? 'semana' : 'semanas'}`
    }
    const months = calculateAgeInMonths(birthDate, referenceDate)
    if (months == null) return null
    if (months < 12) return `${months} ${months === 1 ? 'mês' : 'meses'}`
    const years = Math.floor(months / 12)
    return `${years} ${years === 1 ? 'ano' : 'anos'}`
  }
  if (typeof ageYears === 'number' && ageYears >= 0) {
    if (ageYears < 1) return 'menos de 1 ano'
    return `${ageYears} ${ageYears === 1 ? 'ano' : 'anos'}`
  }
  return null
}

export const getPatientAge = (patient?: Patient): number | null => {
  if (!patient) {
    return null
  }

  if (typeof patient.age === 'number') {
    return patient.age
  }

  if (patient.birthDate) {
    return calculateAgeFromBirthDate(patient.birthDate)
  }

  return null
}

export const normalizePatientSex = (value: string | undefined): PatientSex | undefined => {
  if (!value) {
    return undefined
  }

  const normalized = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()

  if (['f', 'feminino', 'fem', 'mulher'].includes(normalized)) {
    return 'feminino'
  }
  if (['m', 'masculino', 'masc', 'homem'].includes(normalized)) {
    return 'masculino'
  }
  if (['outro', 'nao-binario', 'nao binario'].includes(normalized)) {
    return 'outro'
  }
  if (['nao informado', 'nao_informado', 'ignorado'].includes(normalized)) {
    return 'nao_informado'
  }

  return undefined
}

